import { useCallback, useEffect, useRef, useState } from "react";
import cx from "classnames";
import { useWindowSize } from "../../hooks/useWindowSize";
import {
  freezeScreen,
  useHeroFreezeFrameHeight,
  useHeroSectionHeight,
  videoFrames,
} from "../../hooks/useHeroSectionHeight";
import { useParallaxCaption } from "../../hooks/useParallaxCaption";
import "./Hero.css";

const accelamount = 0.1;

function pad(num: number, size: number) {
  let str = num.toString();
  while (str.length < size) str = "0" + str;
  return str;
}

const preloadImage = (index: number) => {
  const img = new Image();
  img.src = `${process.env.PUBLIC_URL}/hero/01_${pad(index + 1, 5)}.png`;

  return new Promise<HTMLImageElement>((resolve) => {
    img.addEventListener("load", () => resolve(img));
  });
};

const images = new Array(videoFrames).fill(null);
images.map((_, index) => {
  return preloadImage(index).then((img) => {
    images[index] = img;
  });
});

const rawImageWidth = 1920;
const rawImageHeight = 1080;

const Light = () => {
  return (
    <div className="light-container">
      <div className="light"></div>
    </div>
  );
};

const HeroSection = () => {
  const context = useRef<CanvasRenderingContext2D | null>(null);
  const { width, height } = useWindowSize();
  const start = height;
  const sectionHeight = useHeroSectionHeight();
  const freezeFrameHeight = useHeroFreezeFrameHeight();
  const [lastFrame, setLastFrame] = useState(false);
  const show = useParallaxCaption(-freezeScreen) && lastFrame;
  const videoDuration = sectionHeight - freezeFrameHeight;

  const requestID = useRef<number | null>(null);
  const delay = useRef<number>(0);
  const currentFrame = useRef<number>(0);

  const setCurrentFrame = useCallback((frame: number) => {
    currentFrame.current = frame;
    if (frame >= 90) {
      setLastFrame(true);
    } else if (frame) {
      setLastFrame(false);
    }
  }, []);

  const animate = useCallback(
    (time: DOMHighResTimeStamp) => {
      const ctx = context.current;
      if (ctx) {
        const ratio = Math.max(
          0,
          Math.min(1, (window.scrollY - start) / videoDuration)
        );
        const duration = (videoFrames - 1) * ratio;
        delay.current += (duration - delay.current) * accelamount;

        const targetFrame = Math.round(delay.current);

        if (targetFrame !== currentFrame.current) {
          const { width, height } = ctx.canvas;
          ctx.clearRect(0, 0, Math.floor(width), Math.floor(height));
          setCurrentFrame(targetFrame);
          try {
            ctx.drawImage(
              images[targetFrame],
              Math.floor(
                (rawImageWidth - (width * rawImageHeight) / height) / 2
              ),
              0,
              Math.floor((width * rawImageHeight) / height),
              rawImageHeight,
              0,
              0,
              Math.floor(ctx.canvas.width),
              Math.floor(ctx.canvas.height)
            );
          } catch (err) {}
        }

        requestID.current = requestAnimationFrame(animate);
      }
      requestID.current = null;
    },
    [setCurrentFrame, start, videoDuration]
  );

  useEffect(() => {
    async function startAnimation() {
      requestID.current = requestAnimationFrame(animate);
    }

    startAnimation();

    return () =>
      void (requestID.current && cancelAnimationFrame(requestID.current));
  }, [animate, sectionHeight]);

  const setRef = useCallback(
    (ref: HTMLCanvasElement | null) => {
      if (ref) {
        context.current = ref?.getContext("2d") ?? null;
        const canvasHeight = height;
        const canvasWidth = width;
        ref.setAttribute("width", `${canvasWidth}`);
        ref.setAttribute("height", `${canvasHeight}`);
        if (context.current) {
          context.current.imageSmoothingEnabled = false;
        }
      }
    },
    [width, height]
  );

  const setHeroRef = useCallback(
    (ref: HTMLCanvasElement | null) => {
      if (ref) {
        ref.style.height = `calc(${sectionHeight}px + 100vh)`;
      }
    },
    [width, height]
  );

  return (
    <section
      ref={setHeroRef}
      className="section-hero w-full"
      style={{ height: `calc(${sectionHeight}px + 100vh)` }}
    >
      <div
        className="sticky top-0 w-full h-screen overflow-hidden flex justify-center"
        style={{ background: "#181818" }}
      >
        <Light />
        <canvas
          className={cx("canvas h-screen max-w-screen", show && "show")}
          ref={setRef}
        />
      </div>
      <div className="sticky top-0 w-full h-screen container mx-auto text-grid text-center">
        <div
          className={cx(
            "flex-col justify-end flex-1",
            "text-container-1 fade",
            show && "show"
          )}
        >
          <h1 className="text-1 nowrap font-medium whitespace-nowrap inline md:block">
            แตกต่าง
          </h1>
          <h2 className="text-2 nowrap whitespace-nowrap inline md:block">
            ด้วยดีไซน์
          </h2>
        </div>
        <div />
        <div
          className={cx(
            "flex-col justify-end flex-1",
            "text-container-2 fade",
            show && "show"
          )}
        >
          <h2 className="text-3 nowrap whitespace-nowrap">ตอบโจทย์</h2>
          <h1 className="text-4 nowrap font-medium whitespace-nowrap">
            ทุกเจน
          </h1>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
